import React, { useState, useEffect } from "react";
import { bool, string, func, object } from "prop-types";
import { useDispatch } from "react-redux";
import get from "lodash/get";
import { updateUserProfile } from "@quintype/bridgekeeper-js";

import { Icon } from "../../atoms/icon";
import Button from "../../atoms/Button";
import { MEMBER_UPDATED } from "../../store/actions";
import "./profile-edit-modal.m.css";
import EditIcon from "../../../../assets/icons/edit";

const EditableInput = ({
  type = "text",
  placeholder = "",
  value,
  field,
  isEditable,
  onInputChange,
  onToggleEdit,
  disabled = false,
  error = ""
}) => (
  <div>
    <div styleName={`input-container ${isEditable ? 'active' : ''} ${disabled ? 'disabled' : ''} ${error ? 'error' : ''}`}>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onInputChange(field, e.target.value)}
        disabled={disabled || !isEditable}
        styleName={`form-input ${isEditable ? 'active' : ''} ${disabled ? 'disabled' : ''} ${error ? 'error' : ''}`}
      />
      {!disabled && (
        <button
          type="button"
          onClick={() => onToggleEdit(field)}
          styleName="edit-button"
        >
          <EditIcon color="#242424" />
        </button>
      )}
    </div>
    {error && <div styleName="error-message">{error}</div>}
  </div>
);

EditableInput.propTypes = {
  type: string,
  placeholder: string,
  value: string.isRequired,
  field: string.isRequired,
  isEditable: bool.isRequired,
  onInputChange: func.isRequired,
  onToggleEdit: func.isRequired,
  disabled: bool,
  error: string
};

export const ProfileEditModal = ({
  isOpen,
  onClose,
  profileData,
  subscriberMetadata,
  onSave,
  setSubscriberMetadata,
  getSubscriberMetadata,
  setImaSubscriberMetadata,
  className = "",
  ...props
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: ""
  });

  const [editableFields, setEditableFields] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    city: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen && (profileData || subscriberMetadata)) {
      const firstName = get(subscriberMetadata, 'first-name', '');
      const lastName = get(subscriberMetadata, 'last-name', '');

      // If first/last names are empty, try to split the full name
      let defaultFirstName = firstName;
      let defaultLastName = lastName;

      if (!firstName && !lastName) {
        const fullName = get(profileData, 'name') || get(subscriberMetadata, 'name') || '';
        const nameParts = fullName.split(' ');
        defaultFirstName = nameParts[0] || '';
        defaultLastName = nameParts.slice(1).join(' ') || '';
      }

      const { email: profileEmail, phone: profilePhone, city: profileCity } = profileData || {};
      const { email: subscriberEmail, phone: subscriberPhone, city: subscriberCity } = subscriberMetadata || {};

      setFormData({
        firstName: defaultFirstName,
        lastName: defaultLastName,
        email: profileEmail || subscriberEmail || '',
        phone: profilePhone || subscriberPhone || '',
        city: profileCity || subscriberCity || ''
      });

      // Clear validation errors when modal opens
      setValidationErrors({});
    }
  }, [isOpen, profileData, subscriberMetadata]);

  // Separate useEffect to reset success message only when modal initially opens
  useEffect(() => {
    if (isOpen) {
      setShowSuccessMessage(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const toggleEdit = (field) => {
    setEditableFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateRequiredFields = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateRequiredFields()) {
      return;
    }

    setIsLoading(true);

    try {
      // Create full name from first and last name
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();

      // Prepare the body for updateUserProfile
      const updateUserBody = {
        name: fullName,
        email: formData.email,
        'first-name': formData.firstName,
        'last-name': formData.lastName,
        'phone-number': null,
      };

      // Only add phone number if it has a value
      if (formData.phone && String(formData.phone).trim()) {
        updateUserBody['phone-number'] = formData.phone;
      }

      // Call updateUserProfile
      const userProfileResponse = await updateUserProfile(updateUserBody);

      // Prepare metadata for setSubscriberMetadata
      const updatedMetadata = {
        ...subscriberMetadata,
        'name': fullName,
        'first-name': formData.firstName.trim(),
        'last-name': formData.lastName.trim(),
        'phone': formData.phone,
        'location': formData.city,
        'email': formData.email
      };
      await setSubscriberMetadata({ other: updatedMetadata });

      // Fetch fresh subscriber metadata from server
      if (getSubscriberMetadata && setImaSubscriberMetadata) {
        try {
          const response = await getSubscriberMetadata();
          const freshMetadata = response?.data?.other;
          if (freshMetadata) {
            setImaSubscriberMetadata(freshMetadata);
          }
        } catch (error) {
        }
      }

      // Update Redux store
      dispatch({
        type: MEMBER_UPDATED,
        member: get(userProfileResponse, ["user"], null)
      });

      // Call onSave callback if provided
      if (onSave) {
        onSave({
          ...formData,
          name: fullName
        });
      }

      // Show success message instead of closing modal
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
    finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  return (
    <div styleName="modal-overlay" className={className} onClick={handleOverlayClick} {...props}>
      <div styleName="modal-content">
        <button styleName="close-button" onClick={onClose} type="button">
          <Icon name="close" size="xsmall" />
        </button>

        {showSuccessMessage ? (
          <div styleName="success-message-container">
            <div styleName="success-message">
              <h2 styleName="success-title">
                Update submitted<br />successfully
              </h2>
            </div>
          </div>
        ) : (
          <>
            <div styleName="modal-header">
              <h2 styleName="modal-title">Update profile information</h2>
            </div>

            <div styleName="modal-body">
              <div styleName="form-section">
                {/* Email Field (disabled) */}
                <div styleName="form-field email-field">
                  <EditableInput
                    type="email"
                    value={formData.email}
                    field="email"
                    isEditable={false}
                    onInputChange={handleInputChange}
                    onToggleEdit={toggleEdit}
                    disabled
                  />
                </div>

                {/* First Name and Last Name Fields */}
                <div styleName="form-row">
                  <div styleName="form-field">
                    <EditableInput
                      type="text"
                      placeholder="First Name"
                      value={formData.firstName}
                      field="firstName"
                      isEditable={editableFields.firstName}
                      onInputChange={handleInputChange}
                      onToggleEdit={toggleEdit}
                      error={validationErrors.firstName}
                    />
                  </div>

                  <div styleName="form-field">
                    <EditableInput
                      type="text"
                      placeholder="Last Name"
                      value={formData.lastName}
                      field="lastName"
                      isEditable={editableFields.lastName}
                      onInputChange={handleInputChange}
                      onToggleEdit={toggleEdit}
                      error={validationErrors.lastName}
                    />
                  </div>
                </div>

                {/* Phone and City Fields */}
                <div styleName="form-row">
                  <div styleName="form-field">
                    <EditableInput
                      type="tel"
                      value={formData.phone}
                      placeholder="Phone Number"
                      field="phone"
                      isEditable={editableFields.phone}
                      onInputChange={handleInputChange}
                      onToggleEdit={toggleEdit}
                    />
                  </div>

                  <div styleName="form-field">
                    <EditableInput
                      type="text"
                      placeholder="City"
                      value={formData.city}
                      field="city"
                      isEditable={editableFields.city}
                      onInputChange={handleInputChange}
                      onToggleEdit={toggleEdit}
                    />
                  </div>
                </div>
              </div>

              <div styleName="disclaimer">
                <p styleName="disclaimer-text">
                  *Please allow up to 1 week for your profile information to be updated.
                </p>
              </div>

              <div styleName="form-actions">
                <Button
                  variant="dark"
                  onClick={handleSave}
                  styleName="save-button"
                  disabled={isLoading}
                >
                  {isLoading ? "Updating..." : "Request profile update"}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

ProfileEditModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  profileData: object,
  subscriberMetadata: object,
  onSave: func,
  setSubscriberMetadata: func,
  getSubscriberMetadata: func,
  setImaSubscriberMetadata: func,
  className: string
};